import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GradingModal } from '../components/GradingModal';
import {
  BookOpen,
  Download,
  Save,
  Edit2,
  Check,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Award,
  FileText,
  BarChart2,
} from 'lucide-react';
import { toast } from 'sonner';
import { getCurrentUser } from '../../utils/supabase/client';
import { getLessons } from '../../utils/supabase/queries';
import { isAutoGradable } from '../../utils/grading/autoGradeConfig';
import {
  getLessonGradebook,
  updateGrade,
  exportToCSV,
  getStudentActivityGrades,
  updateSubmissionGrade,
  syncLessonAnalytics,
  type GradeSummary
} from '../../utils/supabase/analytics';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { generateGradeReportDocx } from '../../utils/export/docx-generator';
import { generateGradeSummaryPptx } from '../../utils/export/pptx-generator';

type GradeCell = {
  submissionId: string;
  score: number | null;
  maxScore: number;
  graded: boolean;
  slideType: string;
  autoGraded: boolean;
};

export default function Gradebook() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lessons, setLessons] = useState<any[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<string>('');
  const [students, setStudents] = useState<GradeSummary[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [gradeData, setGradeData] = useState<Map<string, Map<string, GradeCell>>>(new Map());
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEditValues, setBulkEditValues] = useState<Record<string, string>>({});
  const [selectedStudent, setSelectedStudent] = useState<{ name: string; sessionId: string } | null>(null);
  const [gradingModalOpen, setGradingModalOpen] = useState(false);

  const calculateTotals = (studentGrades?: Map<string, GradeCell>) => {
    let totalScore = 0;
    let maxPossibleScore = 0;

    if (studentGrades) {
      studentGrades.forEach((gradeCell) => {
        maxPossibleScore += gradeCell.maxScore;
        if (gradeCell.graded && gradeCell.score !== null) {
          // Cap each activity score to its max points
          totalScore += Math.min(gradeCell.score, gradeCell.maxScore);
        }
      });
    }

    const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;

    return { totalScore, maxPossibleScore, percentage };
  };
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;
  const totalPages = Math.ceil(totalRecords / pageSize);

  useEffect(() => {
    loadLessons();
  }, []);

  useEffect(() => {
    if (selectedLesson) {
      loadGradebook();
    }
  }, [selectedLesson, currentPage]);

  const loadLessons = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        toast.error('Please sign in');
        setLoading(false);
        return;
      }

      const lessonsData = await getLessons(user.id);
      // Ensure lessonsData is an array
      const lessonsArray = Array.isArray(lessonsData) ? lessonsData : Object.values(lessonsData || {});
      const publishedLessons = lessonsArray.filter(l => l.status === 'published');
      setLessons(publishedLessons);
      
      if (publishedLessons.length > 0 && !selectedLesson) {
        setSelectedLesson(publishedLessons[0].id);
      } else if (publishedLessons.length === 0) {
        // No lessons available (e.g. shared lessons were deleted by owner)
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading lessons:', error);
      toast.error('Failed to load lessons');
      setLoading(false);
    }
  };

  const loadGradebook = async () => {
    if (!selectedLesson) return;

    setLoading(true);
    try {
      // Sync analytics data first to ensure we have the latest data
      // This is optional - if the sync function fails (e.g., migration not run), continue anyway
      try {
        await syncLessonAnalytics(selectedLesson);
      } catch (syncError) {
        console.warn('Analytics sync failed (migration may not be applied yet):', syncError);
        // Continue loading gradebook even if sync fails
      }
      
      const pagination = { page: currentPage, pageSize };
      const { students: studentsData, activities: activitiesData, total } = 
        await getLessonGradebook(selectedLesson, pagination);
      
      // Debug: log what types we're getting
      console.log('[Gradebook] API Response:', {
        studentsType: Array.isArray(studentsData) ? 'array' : typeof studentsData,
        studentsLength: Array.isArray(studentsData) ? studentsData.length : Object.keys(studentsData || {}).length,
        activitiesType: Array.isArray(activitiesData) ? 'array' : typeof activitiesData,
        activitiesLength: Array.isArray(activitiesData) ? activitiesData.length : Object.keys(activitiesData || {}).length,
      });
      
      // Ensure students and activities are always arrays (convert objects to arrays if needed)
      const studentsArray = Array.isArray(studentsData) ? studentsData : (Object.values(studentsData || {}) as GradeSummary[]);
      const activitiesArray = Array.isArray(activitiesData) ? activitiesData : Object.values(activitiesData || {});
      
      setStudents(studentsArray);
      setActivities(activitiesArray);
      setTotalRecords(total);

      // Load individual activity grades for each student
      const gradeMap = new Map<string, Map<string, GradeCell>>();
      
      for (const student of studentsArray) {
        const studentGradesData = await getStudentActivityGrades(student.session_id, student.student_name);
        // Ensure studentGrades is always an array (convert object to array if needed)
        const studentGrades = Array.isArray(studentGradesData) ? studentGradesData : Object.values(studentGradesData || {});
        const activityMap = new Map<string, GradeCell>();
        
        for (const submission of studentGrades) {
          const slide = (submission as any).slides;
          const autoGraded = isAutoGradable(slide?.type);
          
          activityMap.set(slide?.id, {
            submissionId: submission.id,
            score: submission.score,
            maxScore: slide?.content?.points || slide?.content?.maxPoints || (slide?.type === 'quiz' ? 100 : slide?.type === 'time_to_climb' ? 50 : 10),
            graded: submission.graded,
            slideType: slide?.type || '',
            autoGraded: autoGraded
          });
        }
        
        gradeMap.set(student.student_name, activityMap);
      }
      
      setGradeData(gradeMap);
      
    } catch (error) {
      console.error('Error loading gradebook:', error);
      toast.error('Failed to load gradebook');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCell = (studentName: string, activityId: string, gradeCell: GradeCell) => {
    const hasSubmission = Boolean(gradeCell.submissionId);
    const hasScore = gradeCell.score !== null;

    if (!hasSubmission || (gradeCell.autoGraded && !gradeCell.graded && !hasScore)) {
      toast.error('This activity has not been submitted yet');
      return;
    }
    
    const cellKey = `${studentName}-${activityId}`;
    setEditingCell(cellKey);
    setEditValue((gradeCell.score || 0).toString());
  };

  const handleSaveCell = async (studentName: string, activityId: string, gradeCell: GradeCell) => {
    const score = parseFloat(editValue);
    
    if (isNaN(score) || score < 0 || score > gradeCell.maxScore) {
      toast.error(`Score must be between 0 and ${gradeCell.maxScore}`);
      return;
    }

    setSaving(true);
    try {
      await updateSubmissionGrade(gradeCell.submissionId, score);
      toast.success('Grade updated');
      setEditingCell(null);
      
      // The database trigger will automatically sync grade_summary
      // Just reload the gradebook to get the latest data
      await loadGradebook();
    } catch (error) {
      console.error('Error updating grade:', error);
      toast.error('Failed to update grade');
    } finally {
      setSaving(false);
    }
  };

  const isBulkEditable = (gradeCell: GradeCell) =>
    Boolean(gradeCell.submissionId) &&
    !(gradeCell.autoGraded && !gradeCell.graded && gradeCell.score === null);

  const handleStartBulkEdit = () => {
    const values: Record<string, string> = {};
    let editableCount = 0;

    students.forEach((student) => {
      const studentGrades = gradeData.get(student.student_name);
      activities.forEach((activity) => {
        const gradeCell = studentGrades?.get(activity.id);
        if (gradeCell && isBulkEditable(gradeCell)) {
          values[gradeCell.submissionId] = gradeCell.score === null ? '' : String(gradeCell.score);
          editableCount += 1;
        }
      });
    });

    if (editableCount === 0) {
      toast.info('There are no submitted activity scores to edit on this page');
      return;
    }

    setBulkEditValues(values);
    setBulkEditMode(true);
  };

  const handleCancelBulkEdit = () => {
    setBulkEditMode(false);
    setBulkEditValues({});
  };

  const handleSaveBulkScores = async () => {
    const changes: Array<{ submissionId: string; score: number; studentName: string; activityTitle: string }> = [];

    for (const student of students) {
      const studentGrades = gradeData.get(student.student_name);
      for (const activity of activities) {
        const gradeCell = studentGrades?.get(activity.id);
        if (!gradeCell || !isBulkEditable(gradeCell)) continue;

        const value = bulkEditValues[gradeCell.submissionId] ?? '';
        if (value.trim() === '' && gradeCell.score === null) continue;

        const score = Number(value);
        if (value.trim() === '' || !Number.isFinite(score) || score < 0 || score > gradeCell.maxScore) {
          toast.error(`${student.student_name} — ${activity.title}: score must be between 0 and ${gradeCell.maxScore}`);
          return;
        }
        if (score === gradeCell.score) continue;

        changes.push({
          submissionId: gradeCell.submissionId,
          score,
          studentName: student.student_name,
          activityTitle: activity.title
        });
      }
    }

    if (changes.length === 0) {
      toast.info('No score changes to save');
      return;
    }

    setSaving(true);
    let savedCount = 0;
    const failedChanges: typeof changes = [];

    try {
      for (const change of changes) {
        try {
          await updateSubmissionGrade(change.submissionId, change.score);
          savedCount += 1;
        } catch (error) {
          console.error(`Error updating ${change.studentName}'s score for ${change.activityTitle}:`, error);
          failedChanges.push(change);
        }
      }

      await loadGradebook();

      if (failedChanges.length > 0) {
        toast.error(`Saved ${savedCount} score${savedCount === 1 ? '' : 's'}; ${failedChanges.length} failed. Review and retry the remaining changes.`);
      } else {
        toast.success(`Saved ${savedCount} score${savedCount === 1 ? '' : 's'}`);
        handleCancelBulkEdit();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleViewStudentGrades = (student: GradeSummary) => {
    setSelectedStudent({ name: student.student_name, sessionId: student.session_id });
    setGradingModalOpen(true);
  };

  const handleExport = () => {
    if (students.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = students.map(s => ({
      student_name: s.student_name,
      total_score: s.total_score,
      max_possible_score: s.max_possible_score,
      percentage: s.percentage,
      completed_activities: s.completed_activities,
      total_activities: s.total_activities,
      completion_rate: s.completion_rate
    }));

    exportToCSV(exportData, 'gradebook');
    toast.success('Gradebook exported');
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-green-100 text-green-800 border-green-200';
    if (percentage >= 80) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (percentage >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (percentage >= 60) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card px-4 md:px-6 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Gradebook</h1>
            <p className="text-sm text-muted-foreground mt-1">
              View and manage student grades
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90 w-full sm:w-auto">
                <Download className="w-4 h-4 mr-2" />
                Export Grades
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => {
                if (students.length === 0) {
                  toast.error('No data to export');
                  return;
                }
                const lessonTitle = lessons.find(l => l.id === selectedLesson)?.title || 'Gradebook';
                toast.info('Generating Word document...');
                try {
                  await generateGradeReportDocx({ lessonTitle, students });
                  toast.success('Grade report exported');
                } catch (e) { toast.error('Export failed'); }
              }}>
                <FileText className="w-4 h-4 mr-2" />
                Grade Report (.docx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => {
                if (students.length === 0) {
                  toast.error('No data to export');
                  return;
                }
                const lessonTitle = lessons.find(l => l.id === selectedLesson)?.title || 'Gradebook';
                toast.info('Generating PowerPoint...');
                try {
                  await generateGradeSummaryPptx({ lessonTitle, students });
                  toast.success('Grade summary exported');
                } catch (e) { toast.error('Export failed'); }
              }}>
                <BarChart2 className="w-4 h-4 mr-2" />
                Grade Summary (.pptx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Lesson Selector */}
      <div className="border-b border-border bg-card px-4 md:px-6 py-3">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <Select value={selectedLesson} onValueChange={setSelectedLesson}>
            <SelectTrigger className="w-full md:w-96" disabled={bulkEditMode || saving || editingCell !== null}>
              <SelectValue placeholder="Select a lesson" />
            </SelectTrigger>
            <SelectContent>
              {lessons.map(lesson => (
                <SelectItem key={lesson.id} value={lesson.id}>
                  {lesson.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Grading Legend */}
          <div className="flex items-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200" />
              <span className="text-muted-foreground">Auto-graded</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-100 border border-green-200" />
              <span className="text-muted-foreground">Manually graded</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-100 border border-orange-200" />
              <span className="text-muted-foreground">Pending review</span>
            </div>
          </div>
        </div>
      </div>

      {/* Gradebook Content */}
      <div className="flex-1 overflow-auto p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No students have taken this lesson yet</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <p className="text-sm text-muted-foreground">
                {bulkEditMode
                  ? 'Edit submitted activity scores for students on this page, then save them together.'
                  : 'Need to adjust several activity scores? Edit them together.'}
              </p>
              <div className="flex items-center gap-2">
                {bulkEditMode ? (
                  <>
                    <Button variant="outline" onClick={handleCancelBulkEdit} disabled={saving}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button onClick={handleSaveBulkScores} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save all
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleStartBulkEdit}
                    disabled={saving || editingCell !== null}
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit scores together
                  </Button>
                )}
              </div>
            </div>

            {/* Table header pagination */}
            {totalRecords > pageSize && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
                </p>
                <div className="flex items-center gap-2 self-start sm:self-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1 || bulkEditMode || saving}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages || bulkEditMode || saving}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Desktop Table View */}
            <div className="hidden lg:block w-full rounded-xl border border-border">
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                    <thead className="bg-muted/50 border-b border-border sticky top-0 z-20">
                      <tr>
                        <th className="text-left p-4 font-semibold sticky left-0 z-30 bg-muted/50 border-r border-border min-w-[200px]">
                          Student Name
                        </th>
                        {activities.map((activity, idx) => (
                          <th key={activity.id} className="text-center p-4 font-semibold min-w-[120px] max-w-[160px]">
                            <div className="text-xs mb-1">Activity {idx + 1}</div>
                            <div className="text-xs text-muted-foreground font-normal truncate" title={activity.title}>
                              {activity.title}
                            </div>
                          </th>
                        ))}
                        <th className="text-center p-4 font-semibold sticky right-0 z-30 bg-muted/50 border-l border-border min-w-[120px]">
                          Total Score
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => {
                        const studentGrades = gradeData.get(student.student_name);
                        
                        return (
                          <tr key={student.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="p-4 sticky left-0 z-10 bg-card border-r border-border">
                              <div className="font-medium">{student.student_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {student.completed_activities}/{student.total_activities} completed
                              </div>
                            </td>
                            {activities.map((activity) => {
                              const gradeCell = studentGrades?.get(activity.id);
                              const cellKey = `${student.student_name}-${activity.id}`;
                              const isEditing = editingCell === cellKey;
                              
                              if (!gradeCell) {
                                return (
                                  <td key={activity.id} className="p-2 text-center">
                                    <div className="text-sm text-muted-foreground">—</div>
                                  </td>
                                );
                              }

                              const canBulkEdit = isBulkEditable(gradeCell);
                              const isScored = gradeCell.score !== null;

                              return (
                                <td key={activity.id} className="p-2 text-center">
                                  {bulkEditMode && canBulkEdit ? (
                                    <Input
                                      type="number"
                                      min="0"
                                      max={gradeCell.maxScore}
                                      step="any"
                                      aria-label={`${student.student_name}, ${activity.title} score`}
                                      value={bulkEditValues[gradeCell.submissionId] ?? ''}
                                      onChange={(e) => setBulkEditValues(values => ({
                                        ...values,
                                        [gradeCell.submissionId]: e.target.value
                                      }))}
                                      className="w-20 h-8 text-center text-sm mx-auto"
                                      disabled={saving}
                                    />
                                  ) : isEditing ? (
                                    <div className="flex flex-col items-center gap-1">
                                      <Input
                                        type="number"
                                        min="0"
                                        max={gradeCell.maxScore}
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        className="w-16 h-8 text-center text-sm"
                                        autoFocus
                                        disabled={saving}
                                      />
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0"
                                          onClick={() => handleSaveCell(student.student_name, activity.id, gradeCell)}
                                          disabled={saving}
                                        >
                                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 text-green-600" />}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0"
                                          onClick={() => setEditingCell(null)}
                                          disabled={saving}
                                        >
                                          <X className="w-3 h-3 text-red-600" />
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleEditCell(student.student_name, activity.id, gradeCell)}
                                      className={`group relative px-3 py-2 rounded-lg transition-all ${
                                        gradeCell.autoGraded
                                          ? 'bg-blue-50 hover:bg-blue-100'
                                          : gradeCell.graded
                                          ? 'bg-green-50 hover:bg-green-100'
                                          : 'bg-orange-50 hover:bg-orange-100'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {isScored ? (
                                          <>
                                            <span className="text-sm font-semibold">
                                              {Math.min(gradeCell.score!, gradeCell.maxScore)}/{gradeCell.maxScore}
                                            </span>
                                            {!gradeCell.autoGraded && (
                                              <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            )}
                                          </>
                                        ) : (
                                          <span className="text-xs font-medium text-orange-700">Pending</span>
                                        )}
                                      </div>
                                      {gradeCell.autoGraded && (
                                        <Badge className="absolute -top-1 -right-1 text-[8px] px-1 h-3 bg-blue-500">
                                          AUTO
                                        </Badge>
                                      )}
                                    </button>
                                  )}
                                </td>
                              );
                            })}
                            <td className="p-4 sticky right-0 z-10 bg-card border-l border-border">
                              {(() => {
                                // Keep calculations aligned with grade_summary logic.
                                const { totalScore, maxPossibleScore, percentage } = calculateTotals(studentGrades);
                                
                                return (
                                  <div className="flex flex-col items-center gap-1">
                                    <Badge className={getScoreColor(percentage)}>
                                      {percentage.toFixed(1)}%
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">
                                      {totalScore.toFixed(0)}/{maxPossibleScore.toFixed(0)}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 text-xs mt-1"
                                      onClick={() => handleViewStudentGrades(student)}
                                      disabled={bulkEditMode}
                                    >
                                      View All
                                    </Button>
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              </div>
            </div>

            {/* Mobile/Tablet Card View */}
            <div className="lg:hidden space-y-4">
              {students.map((student) => {
                const studentGrades = gradeData.get(student.student_name);
                
                return (
                  <div key={student.id} className="bg-card border border-border rounded-xl p-4">
                    {(() => {
                      // Keep calculations aligned with grade_summary logic.
                      const { totalScore, maxPossibleScore, percentage } = calculateTotals(studentGrades);
                      
                      return (
                        <>
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="font-semibold">{student.student_name}</h3>
                              <p className="text-xs text-muted-foreground">
                                {student.completed_activities}/{student.total_activities} activities completed
                              </p>
                            </div>
                            <Badge className={getScoreColor(percentage)}>
                              {percentage.toFixed(1)}%
                            </Badge>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Total Score:</span>
                              <span className="font-medium">
                                {totalScore.toFixed(0)} / {maxPossibleScore.toFixed(0)}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Completion:</span>
                              <span className="font-medium">{student.completion_rate.toFixed(0)}%</span>
                            </div>
                          </div>
                        </>
                      );
                    })()}

                    {/* Activity Scores - Summary */}
                    <div className="border-t border-border pt-3 mb-3">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Activity Breakdown ({activities.length} activities)</p>
                      <div className={`grid gap-2 ${activities.length > 4 ? 'max-h-[200px] overflow-y-auto pr-1' : ''}`}>
                        <div className="grid grid-cols-2 gap-2">
                          {activities.map((activity, idx) => {
                            const gradeCell = studentGrades?.get(activity.id);
                            const canBulkEdit = gradeCell ? isBulkEditable(gradeCell) : false;
                            return (
                              <div key={activity.id} className="text-xs flex items-center justify-between bg-muted/30 rounded px-2 py-1.5">
                                <span className="text-muted-foreground truncate flex-1 mr-2" title={activity.title}>
                                  <span className="font-medium text-foreground/70">{idx + 1}.</span> {activity.title.length > 14 ? activity.title.substring(0, 14) + '…' : activity.title}
                                </span>
                                {bulkEditMode && gradeCell && canBulkEdit ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      max={gradeCell.maxScore}
                                      step="any"
                                      aria-label={`${student.student_name}, ${activity.title} score`}
                                      value={bulkEditValues[gradeCell.submissionId] ?? ''}
                                      onChange={(e) => setBulkEditValues(values => ({
                                        ...values,
                                        [gradeCell.submissionId]: e.target.value
                                      }))}
                                      className="w-16 h-7 px-1 text-right text-xs"
                                      disabled={saving}
                                    />
                                    <span className="whitespace-nowrap">/{gradeCell.maxScore}</span>
                                  </div>
                                ) : gradeCell && gradeCell.score !== null ? (
                                  <span className="font-medium whitespace-nowrap">
                                    {Math.min(gradeCell.score, gradeCell.maxScore)}/{gradeCell.maxScore}
                                  </span>
                                ) : (
                                  <span className="whitespace-nowrap text-orange-600">Pending</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleViewStudentGrades(student)}
                      disabled={bulkEditMode}
                    >
                      View & Grade All Activities
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Grade Distribution Summary */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
              {(() => {
                // Calculate grade distribution from gradeData instead of stale database values
                const gradeDistribution = {
                  A: 0, // 90-100%
                  B: 0, // 80-89%
                  C: 0, // 70-79%
                  D: 0, // 60-69%
                  F: 0  // 0-59%
                };
                
                students.forEach((student) => {
                  const studentGrades = gradeData.get(student.student_name);
                  const { percentage } = calculateTotals(studentGrades);
                  
                  if (percentage >= 90) gradeDistribution.A++;
                  else if (percentage >= 80) gradeDistribution.B++;
                  else if (percentage >= 70) gradeDistribution.C++;
                  else if (percentage >= 60) gradeDistribution.D++;
                  else gradeDistribution.F++;
                });
                
                return (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                      <p className="text-xs text-green-700 font-medium">A (90-100%)</p>
                      <p className="text-2xl font-bold text-green-800">
                        {gradeDistribution.A}
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <p className="text-xs text-blue-700 font-medium">B (80-89%)</p>
                      <p className="text-2xl font-bold text-blue-800">
                        {gradeDistribution.B}
                      </p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                      <p className="text-xs text-yellow-700 font-medium">C (70-79%)</p>
                      <p className="text-2xl font-bold text-yellow-800">
                        {gradeDistribution.C}
                      </p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                      <p className="text-xs text-orange-700 font-medium">D (60-69%)</p>
                      <p className="text-2xl font-bold text-orange-800">
                        {gradeDistribution.D}
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                      <p className="text-xs text-red-700 font-medium">F (0-59%)</p>
                      <p className="text-2xl font-bold text-red-800">
                        {gradeDistribution.F}
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Class Statistics */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {(() => {
                // Calculate statistics from gradeData instead of stale database values
                const studentPercentages: number[] = [];
                
                students.forEach((student) => {
                  const studentGrades = gradeData.get(student.student_name);
                  const { percentage } = calculateTotals(studentGrades);
                  studentPercentages.push(percentage);
                });
                
                const classAverage = studentPercentages.length > 0
                  ? studentPercentages.reduce((a, b) => a + b, 0) / studentPercentages.length
                  : 0;
                const highestScore = studentPercentages.length > 0
                  ? Math.max(...studentPercentages)
                  : 0;
                const lowestScore = studentPercentages.length > 0
                  ? Math.min(...studentPercentages)
                  : 0;
                
                return (
                  <>
                    <div className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-5 h-5 text-primary" />
                        <p className="text-sm font-semibold">Class Average</p>
                      </div>
                      <p className="text-3xl font-bold">
                        {classAverage.toFixed(1)}%
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-5 h-5 text-green-600" />
                        <p className="text-sm font-semibold">Highest Score</p>
                      </div>
                      <p className="text-3xl font-bold text-green-600">
                        {highestScore.toFixed(1)}%
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-5 h-5 text-orange-600" />
                        <p className="text-sm font-semibold">Lowest Score</p>
                      </div>
                      <p className="text-3xl font-bold text-orange-600">
                        {lowestScore.toFixed(1)}%
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>

          </>
        )}
      </div>

      {/* Grading Modal */}
      {selectedStudent && (
        <GradingModal
          open={gradingModalOpen}
          onOpenChange={setGradingModalOpen}
          studentName={selectedStudent.name}
          sessionId={selectedStudent.sessionId}
          onGradeUpdated={loadGradebook}
        />
      )}
    </div>
  );
}
